import { useState, useEffect, useCallback } from 'react';
import { http } from '../../../api/http-client';
import { getSessionId } from '../../../services/visitTracker';

export type ReactionType = 'clap' | 'heart' | 'fire' | 'insightful';
export type ReactionCounts = Record<ReactionType, number>;

export const REACTION_EMOJIS: Record<ReactionType, string> = {
  clap: '👏',
  heart: '❤️',
  fire: '🔥',
  insightful: '💡',
};

export const REACTION_LABELS: Record<ReactionType, string> = {
  clap: 'Clap',
  heart: 'Love',
  fire: 'Fire',
  insightful: 'Insightful',
};

interface ReactionsState {
  counts: ReactionCounts;
  myReactions: ReactionType[];
}

const EMPTY: ReactionsState = {
  counts: { clap: 0, heart: 0, fire: 0, insightful: 0 },
  myReactions: [],
};

export function useReactions(blogSlug: string | undefined, postSlug: string | undefined) {
  const [state, setState] = useState<ReactionsState>(EMPTY);
  const [loading, setLoading] = useState(true);
  const sessionId = getSessionId();

  const fetchReactions = useCallback(async () => {
    if (!blogSlug || !postSlug) return;
    try {
      const res = await http.get<ReactionsState>(`/blog/${blogSlug}/${postSlug}/reactions`, {
        params: { sessionId },
      });
      setState(res.data);
    } catch {
      // fail silently
    } finally {
      setLoading(false);
    }
  }, [blogSlug, postSlug, sessionId]);

  useEffect(() => {
    setLoading(true);
    fetchReactions();
  }, [fetchReactions]);

  const toggle = useCallback(
    async (reactionType: ReactionType) => {
      if (!blogSlug || !postSlug) return;

      // Optimistic update
      setState(prev => {
        const hasIt = prev.myReactions.includes(reactionType);
        return {
          counts: {
            ...prev.counts,
            [reactionType]: prev.counts[reactionType] + (hasIt ? -1 : 1),
          },
          myReactions: hasIt
            ? prev.myReactions.filter(r => r !== reactionType)
            : [...prev.myReactions, reactionType],
        };
      });

      try {
        const res = await http.post<ReactionsState>(`/blog/${blogSlug}/${postSlug}/reactions`, {
          sessionId,
          reactionType,
        });
        setState(res.data);
      } catch {
        // Revert on error
        fetchReactions();
      }
    },
    [blogSlug, postSlug, sessionId, fetchReactions]
  );

  return { ...state, loading, toggle };
}
